import { Button } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';

/**
 * PositiveNegativeButtons — Component (L1)
 *
 * Paired confirm/cancel buttons with canonical ordering: Cancel LEFT,
 * positive RIGHT. Standardises action-button ordering across every confirm
 * dialog, sheet, and multi-step flow per the design-system contract
 * (`scrips-signal-ds/contracts/PositiveNegativeButtons.json`).
 *
 * Now composes the Signal `Button` atom — whose docstring states it
 * "consolidates Flutter's Button + IconTextButton + PositiveNegativeButton".
 * The negative slot is `variant="secondary"` (outline), the positive is
 * `variant="primary"` (or `destructive`). State matrix (rest/hover/active/
 * disabled) is owned by Button's variants, so this no longer hand-binds the
 * blue/red ramp utilities. Ordering (Cancel left, positive right) is the
 * value this wrapper still adds over two bare Buttons.
 */

export interface PositiveNegativeButtonsProps {
  /** Action verb on the positive (right-hand) button. Defaults to "Save". */
  positiveLabel?: string;
  /** Label on the negative (left-hand) button. Defaults to "Cancel". */
  negativeLabel?: string;
  onPositive?: () => void;
  onNegative?: () => void;
  /** Greys out the positive button without hiding it — preserves layout while form is invalid. */
  positiveDisabled?: boolean;
  /**
   * Visual register of the positive button.
   * - `primary` → blue interactive scale (default)
   * - `destructive` → red ramp; only for truly destructive actions
   */
  positiveTone?: 'primary' | 'destructive';
  className?: string;
}

export function PositiveNegativeButtons({
  positiveLabel = 'Save',
  negativeLabel = 'Cancel',
  onPositive,
  onNegative,
  positiveDisabled = false,
  positiveTone = 'primary',
  className,
}: PositiveNegativeButtonsProps) {
  return (
    <div
      role="group"
      aria-label={`${negativeLabel} or ${positiveLabel}`}
      className={cn('inline-flex items-center gap-2', className)}
    >
      <Button variant="secondary" size="sm" type="button" onClick={onNegative}>
        {negativeLabel}
      </Button>
      <Button
        variant={positiveTone === 'destructive' ? 'destructive' : 'primary'}
        size="sm"
        type="button"
        onClick={onPositive}
        disabled={positiveDisabled}
      >
        {positiveLabel}
      </Button>
    </div>
  );
}
